import { hasPercentageinKpi, KpiAreaValueType, KpiType } from '../../constants';
import { AreaTargetResponse, KpiAreaDetails, KpiOverviewResponse, KpiTargetSource } from '../../hooks';

const generateRandomInteger = (min: number, max: number) => {
  return Math.floor(min + Math.random() * (max - min + 1));
};

export const widgetNames: KpiType[] = [
  'dailyConversionRate',
  'dailyNewMembers',
  'dailyTurnOver',
  'dailyShareOfReceipts',
  'dailyQueueLength',
  'dailySCOTurnOver',
  'dailySCOShareOfReceipts',
  'dailyCustomerEngagement',
  'dailyRFIDAddedSales',
];

export const MockWidgetKpi: KpiAreaDetails[] = widgetNames.map((widgetName) => {
  let min = null;
  let max = null;
  if (
    widgetName === 'dailyConversionRate' ||
    widgetName === 'dailyNewMembers' ||
    widgetName === 'dailyShareOfReceipts' ||
    widgetName === 'dailyQueueLength'
  ) {
    min = 3;
    max = 40;
  } else {
    min = 200000;
    max = 700000;
  }
  return {
    kpiName: widgetName,
    kpiValues: [
      {
        kpiProperty: 'recommendedTarget' as KpiAreaValueType,
        kpiValue: widgetName === 'dailyNewMembers' ? null : generateRandomInteger(min, max),
      },
      {
        kpiProperty: 'areaAverage' as KpiAreaValueType,
        kpiValue: generateRandomInteger(min, max),
      },
      {
        kpiProperty: 'areaMin' as KpiAreaValueType,
        kpiValue: generateRandomInteger(min, max),
      },
      {
        kpiProperty: 'areaMax' as KpiAreaValueType,
        kpiValue: generateRandomInteger(min, max),
      },
      {
        kpiProperty: 'areaCap' as KpiAreaValueType,
        kpiValue: widgetName === 'dailyTurnOver' ? null : generateRandomInteger(min, max),
      },
    ],
  };
});

export const mockAreaTargets: AreaTargetResponse[] = [
  {
    storeId: 'SE0151',
    currentTarget: 20,
    recommendedTarget: 12,
    storeName: 'H&M - Väsby Centrum - Upplands Väsby',
  },
  {
    storeId: 'SE0110',
    currentTarget: 3,
    recommendedTarget: null,
    storeName: 'H&M - Täby Centrum - Täby',
  },
  {
    storeId: 'SE0421',
    currentTarget: null,
    recommendedTarget: 4,
    storeName: 'H&M - Tuna Park - Eskilstuna',
  },
];

export const mockKpiTargets = () => {
  const distributions = Array.from({ length: 24 }, (_, timeUnit) => {
    const value = Math.random() * 50; // generates a random number between 0 and 50
    return { timeUnit: timeUnit.toString(), value };
  });

  return {
    dailyNewMembers: {
      updated: '2024-04-18',
      target: 12,
      isLocked: false,
      managedIn: 'BOX' as KpiTargetSource,
      distributions: distributions,
    },
    dailyTurnOver: {
      updated: '2024-04-18',
      target: 600000,
      isLocked: false,
      managedIn: 'Synapps' as KpiTargetSource,
      distributions: distributions,
    },
    dailyShareOfReceipts: {
      updated: '2024-04-15',
      target: 15,
      isLocked: true,
      managedIn: 'BOX' as KpiTargetSource,
      distributions: distributions,
    },
    dailyConversionRate: {
      updated: '2024-04-18',
      target: 30,
      isLocked: false,
      managedIn: 'BOX' as KpiTargetSource,
      distributions: distributions,
    },
    dailyQueueLength: {
      updated: '2024-04-18',
      target: 20,
      isLocked: false,
      managedIn: 'BOX' as KpiTargetSource,
      distributions: distributions,
    },
    dailySCOShareOfReceipts: {
      updated: '2024-04-18',
      target: 15,
      isLocked: false,
      managedIn: 'BOX' as KpiTargetSource,
      distributions: distributions,
    },
    dailySCOTurnOver: {
      updated: '2024-04-18',
      target: 700,
      isLocked: false,
      managedIn: 'BOX' as KpiTargetSource,
      distributions: distributions,
    },
    dailyCustomerEngagement: {
      updated: '2024-04-18',
      target: 8,
      isLocked: false,
      managedIn: 'BOX' as KpiTargetSource,
      distributions: distributions,
    },
    dailyRFIDAddedSales: {
      updated: '2024-04-18',
      target: 5,
      isLocked: false,
      managedIn: 'BOX' as KpiTargetSource,
      distributions: distributions,
    },
  };
};

export const MockResponseAreaKpis = () => {
  const areaKpiData = [
    {
      kpiName: 'dailyConversionRate',
      recommendedTarget: 26.83,
      areaAverage: 22.27,
      areaMin: 0,
      areaMax: 48.56943475226797,
      areaCap: 10,
    },
    {
      kpiName: 'dailyTurnOver',
      recommendedTarget: 14309.35,
      areaAverage: 11003.95,
      areaMin: 0,
      areaMax: 34641.00950000001,
      areaCap: 20,
    },
    {
      kpiName: 'dailyNewMembers',
      recommendedTarget: null,
      areaAverage: 0,
      areaMin: 0,
      areaMax: 0,
      areaCap: null,
    },
    {
      kpiName: 'dailyShareOfReceipts',
      recommendedTarget: 53.23,
      areaAverage: 33.49,
      areaMin: 0,
      areaMax: 63.76811594202898,
      areaCap: 40,
    },
    {
      kpiName: 'dailyQueueLength',
      recommendedTarget: 10.5,
      areaAverage: 9.5,
      areaMin: 0,
      areaMax: 20,
      areaCap: 10,
    },
    {
      kpiName: 'dailySCOShareOfReceipts',
      recommendedTarget: 0,
      areaAverage: 0,
      areaMin: 0,
      areaMax: 0,
      areaCap: 0,
    },
    {
      kpiName: 'dailySCOTurnOver',
      recommendedTarget: 0,
      areaAverage: 0,
      areaMin: 0,
      areaMax: 0,
      areaCap: 0,
    },
    {
      kpiName: 'dailyCustomerEngagement',
      recommendedTarget: 7.79,
      areaAverage: 4.2,
      areaMin: 1.67,
      areaMax: 9.7,
      areaCap: 0,
    },
    {
      kpiName: 'dailyRFIDAddedSales',
      recommendedTarget: 0,
      areaAverage: 0,
      areaMin: 0,
      areaMax: 0,
      areaCap: 0,
    },
  ];

  const refactoredKpiTargets: KpiAreaDetails[] = areaKpiData.map((kpi) => ({
    kpiName: kpi.kpiName as KpiType,
    kpiValues: Object.entries(kpi)
      .filter(([key]) => key !== 'kpiName')
      .map(([kpiProperty, kpiValue]) => ({
        kpiProperty: kpiProperty as KpiAreaValueType,
        kpiValue: kpiValue as number | null,
      })),
  }));

  return refactoredKpiTargets;
};

export const mockLastKpi = () => {
  function generateRandomDate(start: Date, end: Date): string {
    const date = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
    return date.toISOString().split('T')[0];
  }

  function generateKpiTargets(count: number): { target: number; startDate: string; endDate: string }[] {
    const targets = [];
    for (let i = 0; i < count; i++) {
      const target = Math.floor(Math.random() * 100);
      const startDate = generateRandomDate(new Date(2024, 0, 1), new Date(2024, 11, 31));
      const endDate = generateRandomDate(new Date(startDate), new Date(2025, 0, 31));
      targets.push({ target, startDate, endDate });
    }
    return targets;
  }

  return {
    current: {
      target: 2000,
      startDate: '2024-04-18',
    },
    last: generateKpiTargets(10),
  };
};

export const mockKpiOverviewData = (): KpiOverviewResponse => {
  const isCurrencyKpi = (kpiName: KpiType) => kpiName === 'dailyTurnOver' || kpiName === 'dailySCOTurnOver';

  const isCount = (kpiName: KpiType) => kpiName === 'dailyNewMembers';

  const getRandomValue = (kpiName: KpiType) => {
    if (hasPercentageinKpi(kpiName)) {
      return Number.parseFloat((Math.random() * 100).toFixed(2));
    }
    if (isCurrencyKpi(kpiName)) {
      return Math.floor(100000 + Math.random() * 900000);
    }
    if (isCount(kpiName)) {
      return Math.floor(Math.random() * 100);
    }
    return 0;
  };

  const kpiDetails: KpiOverviewResponse = widgetNames.reduce((acc, name) => {
    acc[name] = {
      totalTarget: getRandomValue(name),
      averageTarget: getRandomValue(name),
    };
    return acc;
  }, {} as Record<KpiType, { averageTarget: number; totalTarget: number }>);

  // Ensure at least one KPI has a null target
  const randomKpi = widgetNames[Math.floor(Math.random() * widgetNames.length)];
  kpiDetails[randomKpi].totalTarget = 0;
  kpiDetails[randomKpi].averageTarget = 0;

  return kpiDetails;
};
